import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useMediaQuery, useTheme } from '@mui/material';

import TextTransition, { presets } from 'react-text-transition';

import {
  HeroSection as HeroSectionWrapper,
  CarouselContainer,
  PrevNavigationButton,
  NextNavigationButton,
  ContentWrapper,
  TextContent,
  Heading,
  Subtitle,
  SubtitleAnimated,
  Description,
  LightText,
  ButtonWrapper,
  StatusText,
  CardsContainer,
  CardsGrid,
  CardWrapper,
  CardContent,
  CardImageSkeleton,
  CardNameSkeleton,
  CardSpecialtySkeleton,
  CardInfoWrapper,
  AnimatedTextContainer,
} from './styles';
import { habbitsOptions } from '../../constants';
import CTAButton from '../../../../../ui/ctaButton';
import PractitionerCard from './PractitionerCard';
import { openSimplePracticeConsultation } from '../../../../../utils/simplePractice';
import { toTitleCase } from '../../../../../utils/text';

const getCustomFieldValue = (customFields, fieldId) => {
  const field = customFields?.find((field) => field.id === fieldId);
  return field?.value || '';
};

// Fisher-Yates shuffle. Returns a new array; input is not mutated.
// Used to randomize the homepage practitioner carousel so no single
// practitioner is disproportionately featured across page loads.
const shuffleArray = (arr) => {
  if (!Array.isArray(arr) || arr.length <= 1) return arr || [];
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const HeroSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  const CARDS_PER_VIEW = isSmallScreen ? 1 : isMediumScreen ? 2 : isLargeScreen ? 3 : 4;

  const { data, loading } = useSelector((state) => state.practitioners);

  // A unique key generated once per mount. Used to force `useMemo` to
  // produce a fresh shuffle each time the HeroSection remounts (e.g. user
  // navigates away and back in the same session). Combined with the
  // shuffle done in the reducer on every fetch, this gives strong fairness:
  //   - Fresh page load → reducer shuffle → new order
  //   - Same session remount → useMemo shuffle → new order
  //   - Within a single mount → stable order (prev/next don't re-shuffle)
  const [mountSeed] = useState(() => Math.random());

  // Double-shuffle on top of the reducer shuffle to make intra-session
  // navigation also rotate which practitioners are featured first. Memoed on
  // `data` + `mountSeed` so clicking next/prev doesn't re-shuffle mid-use.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shuffledData = useMemo(() => shuffleArray(data), [data, mountSeed]);

  // Start the carousel on a random page (within bounds) so even when a user
  // reloads and coincidentally gets the same shuffle, the first 4 cards
  // they see differ. Only computed when data arrives; zero for empty data.
  const [practitionerIndex, setPractitionerIndex] = useState(0);

  const [currentHabitIndex, setCurrentHabitIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHabitIndex((prev) => (prev + 1) % habbitsOptions.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Randomize the starting carousel page whenever the cards-per-view changes
  // (responsive breakpoint transition) AND whenever the practitioner list
  // first arrives. Using `shuffledData.length` in the dep array gives us a
  // one-shot randomization per fresh data load without re-randomizing on
  // every prev/next click (those update practitionerIndex directly, not
  // the list length).
  useEffect(() => {
    const total = shuffledData?.length || 0;
    if (total === 0) {
      setPractitionerIndex(0);
      return;
    }
    const pages = Math.max(1, Math.ceil(total / CARDS_PER_VIEW));
    setPractitionerIndex(Math.floor(Math.random() * pages));
  }, [CARDS_PER_VIEW, shuffledData.length]);

  const practitionerCards =
    shuffledData?.map((member) => {
      const avatar = getCustomFieldValue(member.customFields, 'Uf5t1LoiYsc9z5teLXBr');
      const bio = getCustomFieldValue(member.customFields, 'xnRAkNyYWeXEiZQryrPV');
      const specialtiesRaw = getCustomFieldValue(member.customFields, '4VbTdvwhnXbJWflbnzbR');

      let specialties = '';
      if (specialtiesRaw) {
        if (typeof specialtiesRaw === 'string') {
          const specialtiesArray = specialtiesRaw
            .split(/[,|]/)
            .map((s) => s.trim())
            .filter((s) => s)
            .map(toTitleCase);
          specialties = specialtiesArray.join(' | ');
        } else if (Array.isArray(specialtiesRaw)) {
          specialties = specialtiesRaw.map(toTitleCase).join(' | ');
        }
      }

      const fullName = member.name || `${member.firstName} ${member.lastName}`;

      return {
        name: fullName,
        specialty:
          specialties || (bio ? bio.substring(0, 50) + '...' : '') || 'Certified Hypnotherapist',
        image: avatar,
        userData: {
          ...member,
          name: fullName,
          firstName: member.firstName,
          lastName: member.lastName,
          bio: bio,
          specialties: specialties,
          profilePhoto: avatar,
        },
      };
    }) || [];

  const totalCards = practitionerCards.length;
  const maxIndex = Math.ceil(totalCards / CARDS_PER_VIEW) - 1;
  const showNavigation = totalCards > CARDS_PER_VIEW;
  // Clamp the window start so the last page always shows a FULL row of cards
  // (it overlaps the previous page slightly) instead of a lonely remainder.
  const maxStart = Math.max(0, totalCards - CARDS_PER_VIEW);
  const startCard = Math.min(practitionerIndex * CARDS_PER_VIEW, maxStart);
  const visibleCards = practitionerCards.slice(startCard, startCard + CARDS_PER_VIEW);

  const handlePractitionerPrevious = () => {
    setPractitionerIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  };

  const handlePractitionerNext = () => {
    setPractitionerIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const handleStartConsultation = () => {
    openSimplePracticeConsultation();
  };

  const handlePractitionerClick = (practitioner) => {
    navigate(`/practitioner-profile/${practitioner.name.replace(/\s+/g, '-').toLowerCase()}`, {
      state: { user: practitioner.userData },
    });
  };

  return (
    <HeroSectionWrapper>
      <ContentWrapper>
        <TextContent>
          <Heading variant="h2" component="h1">
            Your Transformative Journey <br />
            Begins The Moment You Arrive
          </Heading>

          <AnimatedTextContainer>
            <SubtitleAnimated>
              <TextTransition
                springConfig={presets.slow}
                style={{ color: habbitsOptions[currentHabitIndex].color }}
                inline
              >
                {habbitsOptions[currentHabitIndex].text}
              </TextTransition>{' '}
            </SubtitleAnimated>
            <Subtitle>
              <LightText>With Compassionate Certified Practitioners</LightText>
            </Subtitle>
          </AnimatedTextContainer>

          <Description>
            Connect now for a free, confidential online consultation with one of our practitioners.
          </Description>
          <ButtonWrapper>
            <CTAButton
              text="Request a Free 30-Minute Consultation"
              variant="contained"
              onClick={handleStartConsultation}
            />
          </ButtonWrapper>
        </TextContent>

        <CarouselContainer>
          {showNavigation && (
            <PrevNavigationButton
              onClick={handlePractitionerPrevious}
              aria-label="Previous practitioners"
            >
              <ArrowBackIosNewIcon />
            </PrevNavigationButton>
          )}

          {loading ? (
            <CardsContainer>
              <CardsGrid container spacing={{ xs: 2, sm: 2, md: 3 }}>
                {[...Array(CARDS_PER_VIEW)].map((_, index) => (
                  <CardWrapper item xs="auto" key={index}>
                    <CardContent>
                      <CardImageSkeleton variant="rectangular" animation="wave" />
                      <CardInfoWrapper>
                        <CardNameSkeleton variant="text" animation="wave" />
                        <CardSpecialtySkeleton variant="text" animation="wave" />
                      </CardInfoWrapper>
                    </CardContent>
                  </CardWrapper>
                ))}
              </CardsGrid>
            </CardsContainer>
          ) : !data || data.length === 0 ? (
            <StatusText>No practitioners available at the moment.</StatusText>
          ) : (
            <CardsContainer>
              <CardsGrid container spacing={{ xs: 2, sm: 2, md: 3 }}>
                {visibleCards.map((practitioner, index) => (
                  <CardWrapper item xs="auto" key={`${practitioner.image}-${practitionerIndex}`}>
                    <PractitionerCard
                      practitioner={practitioner}
                      onPractitionerClick={handlePractitionerClick}
                    />
                  </CardWrapper>
                ))}
              </CardsGrid>
            </CardsContainer>
          )}

          {showNavigation && (
            <NextNavigationButton onClick={handlePractitionerNext} aria-label="Next practitioners">
              <ArrowForwardIosIcon />
            </NextNavigationButton>
          )}
        </CarouselContainer>
      </ContentWrapper>
    </HeroSectionWrapper>
  );
};

export default HeroSection;
